import { useCallback } from 'react';

import { useQuery } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';

import { pastExportsOptions } from '@/features/DataHub/api';
import {
  ANALYTICS_EVENTS,
  buildCsvWidgetExportEventData
} from '@/features/DataHub/modules/Export/services/ExportAnalyticsService';
import { createUniqueExportNameFromBaseName } from '@/features/DataHub/modules/Export/services/ExportNameService';
import type { CsvWidgetExportSource } from '@/features/DataHub/modules/Export/types/context.types';
import {
  buildSourceBackedDataExportAdditionalParams,
  hasSourceBackedDataExportDateFilter,
  normalizeSourceBackedDataExportFrom
} from '@/features/DataHub/utils/sourceBackedDataExport';
import useSnackbarNotifications from '@/hooks/useSnackbarNotifications';
import type { DateFormattingConfig } from '@/utils/parseUserDateString';
import { triggerBlobDownload } from '@/utils/triggerBlobDownload';

import { prepareCsvWidgetExport } from './downloadCsvWidgetExport';
import { useCsvWidgetExportMutation } from './useCsvWidgetExportMutation';

export const useCsvWidgetExport = (
  source: CsvWidgetExportSource | null,
  tabName: string | undefined,
  dateColumnKey: string | null,
  dateFormattingConfig: DateFormattingConfig
) => {
  const { data: pastExports = [] } = useQuery(pastExportsOptions.list());
  const { mutateAsync } = useCsvWidgetExportMutation();
  const posthog = usePostHog();
  const { showErrorSnackbar } = useSnackbarNotifications();

  const getDefaultExportName = useCallback(
    () =>
      createUniqueExportNameFromBaseName(
        tabName ?? '',
        pastExports.map(e => ({ fileName: e.name })),
        'TradeData'
      ),
    [pastExports, tabName]
  );

  const handleExport = useCallback(
    async (from: string | null, to: string | null, name?: string) => {
      if (!source) {
        throw new Error('CSV widget export source is missing.');
      }

      const requestTo = to ?? null;
      const requestFrom = normalizeSourceBackedDataExportFrom(from, requestTo);

      const exportName = name ?? getDefaultExportName();
      const hasDateFilter = hasSourceBackedDataExportDateFilter(requestFrom, requestTo);

      let mutationStarted = false;
      try {
        posthog.capture(
          ANALYTICS_EVENTS.CSV_WIDGET_EXPORT_STARTED,
          buildCsvWidgetExportEventData(ANALYTICS_EVENTS.CSV_WIDGET_EXPORT_STARTED, {
            exportType: 'trade-csv',
            sourceType: source.sourceType,
            hasDateFilter
          }).data
        );

        const preparedExport = await prepareCsvWidgetExport({
          source,
          from: requestFrom ?? undefined,
          to: requestTo ?? undefined,
          dateColumnKey,
          exportName,
          dateFormattingConfig
        });

        mutationStarted = true;
        await mutateAsync({
          name: exportName,
          exportType: 'csv',
          source,
          from: requestFrom,
          to: requestTo,
          tabName,
          additionalParams: buildSourceBackedDataExportAdditionalParams(source)
        });

        triggerBlobDownload(preparedExport.blob, preparedExport.fileName);

        posthog.capture(
          ANALYTICS_EVENTS.CSV_WIDGET_EXPORT_COMPLETED,
          buildCsvWidgetExportEventData(ANALYTICS_EVENTS.CSV_WIDGET_EXPORT_COMPLETED, {
            exportType: 'trade-csv',
            sourceType: source.sourceType,
            hasDateFilter
          }).data
        );
      } catch (err) {
        posthog.capture(
          ANALYTICS_EVENTS.CSV_WIDGET_EXPORT_FAILED,
          buildCsvWidgetExportEventData(ANALYTICS_EVENTS.CSV_WIDGET_EXPORT_FAILED, {
            exportType: 'trade-csv',
            sourceType: source.sourceType,
            hasDateFilter
          }).data
        );
        // Mutation's onError handles the snackbar when the mutation itself fails.
        // If the error occurred before the mutation started (e.g. prepareCsvWidgetExport
        // failed), we must show the snackbar here since onError will never fire.
        if (!mutationStarted) {
          showErrorSnackbar(
            'Oops! Something went wrong. Please try again later.',
            3000,
            'trade-export-error'
          );
        }
        throw err;
      }
    },
    [
      source,
      getDefaultExportName,
      dateColumnKey,
      mutateAsync,
      posthog,
      showErrorSnackbar,
      dateFormattingConfig,
      tabName
    ]
  );

  return { handleExport, getDefaultExportName };
};
